import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import LocationPicker from '../components/LocationTree';
import { resizeImage } from '../utils/resizeImage';
import './ItemForm.css';

function ItemForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isEditMode = !!id;
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    count: 1,
    image: null,
    storageLocationId: searchParams.get('locationId') || ''
  });
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(isEditMode);
  const fileInputRef = useRef();
  const videoRef = useRef();
  const streamRef = useRef(null);
  const canvasRef = useRef();
  const [useCamera, setUseCamera] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (isEditMode && id) {
      fetchItemData();
    }
  }, [id, isEditMode]);

  const fetchItemData = async () => {
    try {
      const response = await fetch(`/api/items/${id}`);
      const data = await response.json();
      setFormData({
        name: data.name,
        description: data.description || '',
        count: data.count || 1,
        image: data.image ? `data:${data.imageMimeType};base64,${data.image}` : null,
        storageLocationId: data.storageLocationId || ''
      });
      if (data.image) {
        setPreview(`data:${data.imageMimeType};base64,${data.image}`);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching item:', error);
      setError('Failed to load item');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'count' ? parseInt(value) || 1 : value
    }));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const resized = await resizeImage(e.target.result);
          setFormData(prev => ({
            ...prev,
            image: resized
          }));
          setPreview(resized);
        } catch (err) {
          setError('Unable to read image');
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const startCamera = async () => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      setUseCamera(true);
    } catch (err) {
      setError('Unable to access camera');
      console.error(err);
    }
  };

  // The <video> element only exists once useCamera is true, so attach the stream after that render
  useEffect(() => {
    if (useCamera && videoRef.current && streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
    }
  }, [useCamera]);

  // Release the camera if the page is left while it is on
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
  }, []);

  const capturePhoto = async () => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    // Match the camera's real aspect ratio so the photo isn't stretched to 4:3
    canvas.width = video.videoWidth || canvas.width;
    canvas.height = video.videoHeight || canvas.height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = await resizeImage(canvas.toDataURL('image/jpeg'));
    setFormData(prev => ({
      ...prev,
      image: imageData
    }));
    setPreview(imageData);
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setUseCamera(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Item name is required');
      return;
    }

    try {
      const url = isEditMode ? `/api/items/${id}` : '/api/items';
      const method = isEditMode ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          count: formData.count,
          image: formData.image ? (typeof formData.image === 'string' && formData.image.includes('base64') 
            ? formData.image.split(',')[1] || formData.image.split(';')[1].split(',')[1]
            : formData.image.split(',')[1]) : null,
          imageMimeType: formData.image ? 'image/jpeg' : null,
          storageLocationId: formData.storageLocationId || null
        })
      });

      if (response.ok) {
        navigate(formData.storageLocationId ? `/locations/${formData.storageLocationId}` : '/items');
      } else {
        const errorData = await response.json();
        setError(errorData.error || `Failed to ${isEditMode ? 'update' : 'create'} item`);
      }
    } catch (err) {
      setError(`Error ${isEditMode ? 'updating' : 'creating'} item: ` + err.message);
    }
  };

  return (
    <div className="form-container">
      <div className="form-card">
        <h1>{isEditMode ? 'Edit Item' : 'Create Item'}</h1>
        
        {loading && <p>Loading item...</p>}
        {error && <div className="error-message">{error}</div>}

        {!loading && (
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Item Name *</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="e.g., Camping Equipment"
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Describe this item..."
              rows="3"
            ></textarea>
          </div>

          <div className="form-group">
            <label>Quantity</label>
            <input
              type="number"
              name="count"
              value={formData.count}
              onChange={handleInputChange}
              min="1"
            />
          </div>

          <div className="form-group">
            <label>Storage Location (Optional)</label>
            <LocationPicker
              value={formData.storageLocationId}
              onChange={(locationId) => setFormData(prev => ({ ...prev, storageLocationId: locationId }))}
              topLabel="No location (unassigned)"
              title="Storage location"
            />
          </div>

          <div className="form-group">
            <label>Item Image</label>
            
            {useCamera ? (
              <div className="camera-section">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  className="video-preview"
                ></video>
                <canvas
                  ref={canvasRef}
                  width="640"
                  height="480"
                  style={{ display: 'none' }}
                ></canvas>
                <div className="camera-buttons">
                  <button type="button" onClick={capturePhoto} className="btn btn-primary">
                    Capture Photo
                  </button>
                  <button type="button" onClick={stopCamera} className="btn btn-secondary">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                {preview && (
                  <div className="image-preview">
                    <img src={preview} alt="Item" />
                    <button
                      type="button"
                      onClick={() => {
                        setPreview(null);
                        setFormData(prev => ({ ...prev, image: null }));
                      }}
                      className="remove-btn"
                    >
                      ✕
                    </button>
                  </div>
                )}
                <div className="image-upload">
                  <button
                    type="button"
                    onClick={() => fileInputRef.current.click()}
                    className="btn btn-secondary"
                  >
                    Upload Photo
                  </button>
                  <button
                    type="button"
                    onClick={startCamera}
                    className="btn btn-secondary"
                  >
                    Take Photo
                  </button>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />
              </>
            )}
          </div>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {isEditMode ? 'Update Item' : 'Create Item'}
            </button>
            <button
              type="button"
              onClick={() => navigate(formData.storageLocationId ? `/locations/${formData.storageLocationId}` : '/items')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

export default ItemForm;
